import { memo, useState, useCallback, useRef } from "react";
import type { ReactNode } from "react";
import type { TViewerProps } from "@modules/viewers/types";
import type { TPdfPageMeta } from "@modules/viewers/pdf/types";
import type { PdfPageMetrics } from "@core/types";
import { usePdfDocument } from "@modules/viewers/pdf/hooks";
import { PdfToolbar } from "@modules/viewers/pdf/components/PdfToolbar";
import { PdfMainViewer } from "@modules/viewers/pdf/components/PdfMainViewer";
import { PdfThumbnailSidebar } from "@modules/viewers/pdf/components/PdfThumbnailSidebar";
import { PdfPageSkeleton } from "@modules/viewers/pdf/components/PdfPageSkeleton";

const SCALE_MIN = 0.5;
const SCALE_MAX = 3;
const SCALE_STEP = 0.25;

type PdfViewerProps = TViewerProps & {
  // Annotation render props — optional, PdfViewer works standalone without them
  renderAnnotationToolbar?: (pageMetrics: PdfPageMetrics) => ReactNode;
  renderShapeOverlay?: (
    pageMetrics: PdfPageMetrics,
    scale: number,
    totalPages: number,
  ) => ReactNode;
};

export const PdfViewer = memo(function PdfViewer({
  url,
  renderAnnotationToolbar,
  renderShapeOverlay,
}: PdfViewerProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [pdfDoc, setPdfDoc] = useState<TPdfPageMeta | null>(null);
  const [pageMetrics, setPageMetrics] = useState<PdfPageMetrics | null>(null);

  const mainViewerRef = useRef<HTMLDivElement>(null);

  const { data: pdfBlob } = usePdfDocument(url);
  // pdfBlob guards below ensure pdfjs never receives the raw URL.
  // When the URL is passed directly, pdfjs appends /stream internally and the
  // worker fetch loses auth cookies → transient 401 before the blob is ready.
  // Holding off until the blob arrives means pdfjs only ever sees binary data.

  const handleZoomIn = useCallback(() => {
    setScale((prev) => Math.min(prev + SCALE_STEP, SCALE_MAX));
  }, []);

  const handleZoomOut = useCallback(() => {
    setScale((prev) => Math.max(prev - SCALE_STEP, SCALE_MIN));
  }, []);

  const handleRotate = useCallback(() => {
    setRotation((prev) => (prev + 90) % 360);
  }, []);

  const handleDocumentLoad = useCallback((meta: TPdfPageMeta) => {
    setTotalPages(meta.numPages);
    setPdfDoc(meta);
  }, []);

  const handleFullscreen = useCallback(() => {
    const target = mainViewerRef.current;
    if (!target) return;
    if (!document.fullscreenElement) {
      target.requestFullscreen();
      return;
    }
    document.exitFullscreen();
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePrevPage = useCallback(() => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  }, []);

  const handleNextPage = useCallback(() => {
    setCurrentPage((prev) => Math.min(totalPages, prev + 1));
  }, [totalPages]);

  // suppress unused warning until approval submit is wired in next step
  void pdfDoc;

  return (
    <div className="flex flex-1 flex-col gap-3 overflow-hidden bg-[#fdfdfd] px-4 py-3 md:px-10 lg:px-16">
      <PdfToolbar
        currentPage={currentPage}
        totalPages={totalPages}
        scale={scale}
        rotation={rotation}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onRotate={handleRotate}
        onPageChange={handlePageChange}
        onPrevPage={handlePrevPage}
        onNextPage={handleNextPage}
        onFullscreen={handleFullscreen}
      />

      {pageMetrics && renderAnnotationToolbar?.(pageMetrics)}

      <div className="flex flex-1 gap-[11px] overflow-hidden">
        {pdfBlob && (
          <PdfThumbnailSidebar
            pdfFile={pdfBlob}
            totalPages={totalPages}
            currentPage={currentPage}
            onPageClick={handlePageChange}
          />
        )}

        {/* Scroll container — mainViewerRef lives here; ShapeOverlay is absolute inside */}
        <div
          ref={mainViewerRef}
          className="relative flex-1 min-w-0 overflow-auto border border-neutral-200 rounded-xl bg-white"
        >
          {pdfBlob ? (
            <PdfMainViewer
              pdfFile={pdfBlob}
              currentPage={currentPage}
              totalPages={totalPages}
              scale={scale}
              rotation={rotation}
              searchMatches={[]}
              activeMatchIndex={-1}
              onDocumentLoad={handleDocumentLoad}
              onPageChange={handlePageChange}
              mainViewerRef={mainViewerRef}
              onPageMetricsChange={setPageMetrics}
            />
          ) : (
            <div className="flex items-center justify-center py-8">
              <PdfPageSkeleton />
            </div>
          )}
          {pageMetrics &&
            renderShapeOverlay?.(pageMetrics, scale, totalPages)}
        </div>
      </div>
    </div>
  );
});
